import { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import {
  usePanelContext,
  useShouldPanelBeVisible,
} from '~/eds/src/components/Panel/PanelContext';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { useDebouncedValue } from '~/eds/src/hooks/useDebouncedValue';
import { api } from '~/redux/api/createApi';
import { BatchAddGroupNodeItem } from '~/redux/api/methods/group-nodes/types';

import { getNodeIconName } from '../helpers/getResourceIcon';
import { EnrichedGroupNode } from '../helpers/NodeEnricher/NodeEnricher';
import { TreeBrowserNode } from '../helpers/TreeBrowser/types';
import { useTreeBrowser } from '../helpers/TreeBrowser/useTreeBrowser';
import { AddToGroupFooter } from './AddToGroupFooter';
import { AddToGroupOverlay } from './AddToGroupOverlay';
import { GroupSelection } from './types';

const messages = defineMessages({
  addDocumentPanelTitle: {
    id: 'AddToGroupPanel.addMultipleDocumentsPanelTitle',
    defaultMessage:
      '{count, plural, one {Add document to...} other {Add # documents to...}}',
    description: 'Title for add to group panel, with singular/plural handling',
  },
  addGroupAndParentDescription: {
    id: 'AddItemsPanel.addGroupAndParentDescription',
    defaultMessage:
      'Add documents to a group and link to a Parent to track related work. Note: completed tickets will be added as the latest document version.',
    description: 'Description for the add group and parent page',
  },
  groupLabel: {
    id: 'AddToGroupPanel.groupLabel',
    defaultMessage: 'Group',
    description: 'Label for group selection field',
  },
  groupPlaceholder: {
    id: 'AddToGroupPanel.groupPlaceholder',
    defaultMessage: 'Select a group...',
    description: 'Placeholder for group selection field',
  },
  parentNodeLabel: {
    id: 'AddToGroupPanel.parentNodeLabel',
    defaultMessage: 'Parent (Optional)',
    description: 'Label for parent node dropdown',
  },
  parentNodePlaceholder: {
    id: 'AddToGroupPanel.parentNodePlaceholder',
    defaultMessage: 'Select a parent...',
    description: 'Placeholder for parent node dropdown',
  },
  parentNodeHint: {
    id: 'AddToGroupPanel.parentNodeHint',
    defaultMessage: 'Select a group to link to a Parent',
    description: 'Hint shown under parent dropdown when no group is selected',
  },
  singleDocumentAdded: {
    id: 'AddToGroupPanel.singleDocumentAdded',
    defaultMessage: 'Document added to {groupName}',
    description: 'Message when single document is added to group',
  },
  multipleDocumentsAdded: {
    id: 'AddToGroupPanel.multipleDocumentsAdded',
    defaultMessage: 'Added {count} documents to {groupName}',
    description: 'Message when multiple documents are added to group',
  },
});

export const AddToGroupPanelId = 'add-to-group';

interface AddToGroupPanelProps {
  preselectedItems: BatchAddGroupNodeItem[];
  onClose?: () => void;
}

export const AddToGroupPanel = ({
  preselectedItems,
  onClose,
}: AddToGroupPanelProps) => {
  const { formatMessage } = useIntl();
  const { closePanel } = usePanelContext();
  const show = useShouldPanelBeVisible(AddToGroupPanelId);
  const [selectedGroup, setSelectedGroup] = useState<GroupSelection | null>(
    null,
  );
  const [selectedParentNode, setSelectedParentNode] = useState<string | null>(
    null,
  );
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [nodes, setNodes] = useState<TreeBrowserNode<EnrichedGroupNode>[]>([]);
  const [keepFormOpen, setKeepFormOpen] = useState(false);
  const [groupSearchText, setGroupSearchText] = useState('');
  const debouncedGroupSearch = useDebouncedValue(groupSearchText);

  const { treeBrowser } = useTreeBrowser({
    rootNodeId: selectedGroup?.rootNodeId,
    groupId: selectedGroup?.id,
  });

  const { data: allGroups = [], isFetching: isLoadingGroups } =
    api.endpoints.getAllGroups.useQuery(
      debouncedGroupSearch ? { search: debouncedGroupSearch } : {},
    );

  const [createGroup] = api.endpoints.createGroup.useMutation();

  const [batchAddNodes, batchAddNodesResult] =
    api.endpoints.batchAddNodes.useMutation();

  useEffect(() => {
    if (selectedGroup) {
      setSelectedParentNode(null);
      setNodes([]);
    }
  }, [selectedGroup?.id]);

  const groupOptions = useMemo(() => {
    return allGroups.map((group) => ({
      label: group.attributes?.name,
      value: group.id,
      leftIcon: 'folder' as const,
    }));
  }, [allGroups]);

  const parentOptions = useMemo(
    () =>
      nodes.map((node) => ({
        label: node.enrichedFields?.name,
        value: node.id,
        leftIcon: getNodeIconName(node) as any,
      })),
    [nodes],
  );

  const handleClose = () => {
    setSelectedGroup(null);
    setSelectedParentNode(null);
    setNodes([]);
    closePanel(AddToGroupPanelId);
    onClose?.();
  };

  const loadParentOptions = async () => {
    if (!treeBrowser || !selectedGroup?.rootNodeId) {
      return;
    }

    setIsLoadingNodes(true);
    try {
      await treeBrowser.loadAllNodes();
      setNodes(
        treeBrowser
          .getAllNodes()
          .filter((node) => node.id !== selectedGroup.rootNodeId),
      );
    } catch {
      setNodes([]);
    } finally {
      setIsLoadingNodes(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedGroup?.rootNodeId || preselectedItems.length === 0) {
      return;
    }

    const count = preselectedItems.length;
    const groupName = selectedGroup.groupName || 'group';
    const successMessage =
      count === 1
        ? formatMessage(messages.singleDocumentAdded, { groupName })
        : formatMessage(messages.multipleDocumentsAdded, {
            count,
            groupName,
          });

    await batchAddNodes({
      parentNodeId: selectedParentNode ?? selectedGroup.rootNodeId,
      items: preselectedItems,
      rtkQueryOptions: {
        showSuccessToast: successMessage,
      },
    }).unwrap();

    if (keepFormOpen) {
      setSelectedGroup(null);
      setSelectedParentNode(null);
      setNodes([]);
    } else {
      handleClose();
    }
  };

  if (!show || preselectedItems.length === 0) {
    return null;
  }

  const isActionDisabled = !selectedGroup || batchAddNodesResult.isLoading;

  return (
    <>
      <AddToGroupOverlay isVisible={batchAddNodesResult.isLoading} />
      <Panel
        animate
        enableBackdrop
        mode="floating"
        position="right"
        title={formatMessage(messages.addDocumentPanelTitle, {
          count: preselectedItems.length,
        })}
        width="l"
        hidden={{
          isHidden: false,
          onHide: handleClose,
        }}
        footer={{
          children: (
            <AddToGroupFooter
              onBack={handleClose}
              onNext={handleSubmit}
              isActionDisabled={isActionDisabled}
              keepFormOpen={keepFormOpen}
              onChangeKeepFormOpen={setKeepFormOpen}
            />
          ),
        }}
      >
        <Layout
          direction="column"
          style={{
            height: '100%',
            justifyContent: 'stretch',
            overflow: 'hidden',
            gap: '16px',
          }}
        >
          <Text color="text.secondary">
            {formatMessage(messages.addGroupAndParentDescription)}
          </Text>

          <Layout direction="column" spacing={4}>
            <Layout direction="column" spacing={1}>
              <Text variant="body-bold">
                {formatMessage(messages.groupLabel)}
              </Text>
              <Select
                isMulti={false}
                isSearchable
                enableSearchIcon
                isLoading={isLoadingGroups}
                options={groupOptions}
                placeholder={formatMessage(messages.groupPlaceholder)}
                isClearable={false}
                name="group"
                onSearch={setGroupSearchText}
                value={selectedGroup?.id ?? null}
                onAddOption={async ({ label: newGroupName }) => {
                  const newGroup = await createGroup({
                    name: newGroupName.trim(),
                  }).unwrap();
                  const rootNodeId = (newGroup as any)?.relationships?.rootNode
                    ?.data?.id as string | undefined;
                  if (newGroup && rootNodeId) {
                    setSelectedGroup({
                      id: newGroup.id,
                      rootNodeId,
                      groupName: newGroup.attributes?.name || newGroupName,
                    });
                  }
                }}
                onChange={(groupId) => {
                  const group = allGroups.find((g) => g.id === groupId);

                  if (!group) {
                    setSelectedGroup(null);
                    return;
                  }

                  const rootNodeId = (group as any).relationships?.rootNode
                    ?.data?.id as string | undefined;
                  if (rootNodeId) {
                    setSelectedGroup({
                      id: group.id,
                      rootNodeId,
                      groupName: group.attributes?.name || 'group',
                    });
                  }
                }}
              />
            </Layout>

            <Layout direction="column" spacing={1}>
              <Text variant="body-bold">
                {formatMessage(messages.parentNodeLabel)}
              </Text>
              <Select
                isMulti={false}
                isLoading={isLoadingNodes}
                isSearchable
                enableSearchIcon
                onFocus={loadParentOptions}
                options={parentOptions}
                placeholder={formatMessage(messages.parentNodePlaceholder)}
                isClearable
                disabled={!selectedGroup}
                name="parent-node"
                value={selectedParentNode}
                onChange={(value) =>
                  setSelectedParentNode(value as string | null)
                }
              />
              <Text variant="body" color="text.secondary">
                {formatMessage(messages.parentNodeHint)}
              </Text>
            </Layout>
          </Layout>
        </Layout>
      </Panel>
    </>
  );
};
